import { Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';

import { AuditLoggingService } from './shared/logging/audit-logging.service';
import { AuthenticationService } from './shared/authentication/authentication.service';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { BreadcrumbService } from './breadcrumb/breadcrumb.service';
import { ConfigurationService } from './configuration/services/configuration.service';
import { GenericErrorHandlingService } from './shared/generic-error-handling/generic-error-handling.service';
import { GenericModalComponent } from './shared/generic-modal/generic-modal.component';
import { GlobalNotificationPanelComponent } from './shared/global-notification-panel/global-notification-panel.component';
import { LoadingPanelComponent } from './shared/loading-panel/loading-panel.component';
import { locale } from 'devextreme/localization';
import { LoginObserverService } from './shared/authentication/login-observer.service';
import { MainMenuComponent } from './mainmenu/mainmenu.component';
import { Svg4EveryoneService } from './shared/icons/svg-4-everyone.service';
import { SystemService } from './system/services/system.service';
import { ToastComponent } from './shared/toast/toast.component';
import { TransportOutOverrideService } from './transport/services/transport-out-override.service';
import { UserService } from './user/services/user.service';
import { UserStickerMenuComponent } from './user/user-sticker-menu.component';
import { ApplicationInsightsService } from './shared/insights/application-insights.service';
import { ProductFruitsService } from './shared/product-fruits.service';
import { CurrentUserService } from './user/services';
import { distinctUntilChanged } from 'rxjs/operators';
import { ProductFruitsUserObject } from 'product-fruits';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'ep-app',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [UserStickerMenuComponent, MainMenuComponent, BreadcrumbComponent, RouterOutlet, GenericModalComponent, ToastComponent, LoadingPanelComponent, GlobalNotificationPanelComponent]
})
export class AppComponent implements OnInit, OnDestroy {
    private appInsightsService = inject(ApplicationInsightsService);
    private productFruitsService = inject(ProductFruitsService);
    private currentUserService = inject(CurrentUserService);
    private auditLoggingService = inject(AuditLoggingService);
    private authenticationService = inject(AuthenticationService);
    private breadcrumbService = inject(BreadcrumbService);
    private configurationService = inject(ConfigurationService);
    private errorHandling = inject(GenericErrorHandlingService);
    private loginObserverService = inject(LoginObserverService);
    private router = inject(Router);
    private svg4EveryoneService = inject(Svg4EveryoneService);
    private systemService = inject(SystemService);
    private titleService = inject(Title);
    private transportOutOverrideService = inject(TransportOutOverrideService);
    private userService = inject(UserService);
    private destroyRef = inject(DestroyRef);

    constructor() {
        locale('en');
        this.appInsightsService.logTrace('Link3 UI Application started');
    }

    isUserLoggedIn = false;
    breadcrumb = '';
    environment = '';

    @ViewChild('mainMenu') mainMenu: MainMenuComponent;

    onClick(): void {
        if (this.mainMenu) {
            this.mainMenu.hideMenu();
        }
    }

    ngOnInit(): void {
        void this.initialize();
    }

    private async initialize(): Promise<void> {
        if (this.checkIfIE()) {
            await this.router.navigate(['unsupported-browser']);
            return;
        }

        this.loginObserverService.loginObserver.subscribe(() => {
            this.isUserLoggedIn = true;
            this.setEnvironment();
        });

        this.loginObserverService.logoutObserver.subscribe(() => {
            this.isUserLoggedIn = false;
        });

        await this.authenticationService.startup();

        this.errorHandling.subscribeToErrors();

        const loggedIn = await this.authenticationService.isLoggedIn();
        this.isUserLoggedIn = !!loggedIn;
        if (loggedIn) {
            void this.userService.refreshPermissionsForCurrentUser();
            this.setEnvironment();
        }
        this.svg4EveryoneService.run();

        this.breadcrumbService.breadcrumbObserver.subscribe(breadcrumbs => {
            this.breadcrumb = breadcrumbs.map(breadcrumb => breadcrumb.label).join(' / ');
            this.setTitle();
        });

        void this.systemService.startVersionCheckTimer();

        this.auditLoggingService.startObserverSubsriptions();

        void this.userService.loadSimpleUsers();

        void this.transportOutOverrideService.start();

        await this.initializeProductFruits();
    }

    ngOnDestroy(): void {
        this.productFruitsService.destroy();
    }

    checkIfIE() {
        // Code from here: https://github.com/angular/components/blob/e99ca0ac9b0088d20d5d680092cd7ea5624934c0/src/cdk/platform/platform.ts
        return (/(msie|trident)/i.test(navigator.userAgent));
    }

    private async initializeProductFruits(): Promise<void> {
        const { WorkspaceCode, Language, TenantId } = await this.configurationService.getProductFruitsConfig();

        if (WorkspaceCode?.length > 0 && Language?.length > 0 && TenantId?.length > 0) {
            this.currentUserService.userChangedObserver.pipe(
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            ).subscribe(user => {
                this.productFruitsService.destroy();
                let userObjectForProductFruits = this.createUserObjectForProductFruits(TenantId, null, null);
                if (user) {
                    userObjectForProductFruits = this.createUserObjectForProductFruits(TenantId, user.UserId, user.EmailAddress);
                }
                void this.productFruitsService.inititialize(WorkspaceCode, Language, userObjectForProductFruits);
                console.log(`Product Fruits initialized with user: ${userObjectForProductFruits.username}`, userObjectForProductFruits);
            });
        }
    }

    private createUserObjectForProductFruits(tenantId: string, userId: number | null, email: string | null): ProductFruitsUserObject {
        const userObject = { username: `${tenantId}:${userId ?? 'anonymous'}`, email };
        return userObject;
    }

    private setTitle() {
        let title = 'Link.';

        if (this.environment !== '') {
            title += ' [' + this.environment + ']';
        }

        if (this.breadcrumb !== '') {
            title += ' - [' + this.breadcrumb + ']';
        }

        this.titleService.setTitle(title);
    }

    private setEnvironment() {
        this.configurationService.getActiveEnvironment().then(result => {
            if (result) {
                this.environment = result;
                this.setTitle();
            }
            else {
                console.log('Returned environment type is null or undefined...');
            }
        }).catch(() => { console.log('Could not load environment type...'); });
    }
}
